import { Sidebar as Sidebr, Menu, MenuItem, SubMenu } from "react-pro-sidebar";
import { Link, useLocation } from "react-router-dom";
import "./sidebar.scss";
import React, { useEffect, useMemo, useState } from "react";
import { getSideBarActiveDetails } from "../../utils/commonUtils";
import useAdmin from "../../Hooks/useAdmin";
import {
  getMenuItems,
  getPermissionsList,
  selectedRoleId,
  setMenuItems,
  setPermissions,
} from "../../redux/auth/authSlice";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import Loading from "../../components/LoadingPage/Loading";
import useRolesAndPermissions from "../../Hooks/useRolesAndPermissions";
import { getIconClassName } from "../../utils/getIconPath";

interface SidebarProps {
  isCollapsed: boolean;
}

interface IRecursiveMenuItemProps {
  menuItem: IMenuItem;
  activeMenuPath: string | undefined;
  openMenus: Record<number, string | null>;
  setOpenMenus: (
    updater: (
      prev: Record<number, string | null>
    ) => Record<number, string | null>
  ) => void;
  level?: number;
  parentMenuIcon?: string;
}

interface IMenuItem {
  name: string;
  menuKey: string;
  icon?: string;
  route?: string;
  path?: string;
  childMenus?: IMenuItem[];
}

const RecursiveMenuItem: React.FC<IRecursiveMenuItemProps> = ({
  menuItem,
  activeMenuPath,
  openMenus,
  setOpenMenus,
  level = 0,
  parentMenuIcon,
}) => {
  const hasChildren = menuItem.childMenus && menuItem.childMenus.length > 0;

  const className = useMemo(() => {
    if (level === 0) return "main-menu-items";
    if (level === 1 && !hasChildren) return "submenu-items";
    if (hasChildren) return "child-submenu-items";
    return "child-menu-items";
  }, [level, hasChildren]);

  const iconClassName = useMemo(() => {
    return getIconClassName(parentMenuIcon || menuItem?.icon || "");
  }, [menuItem.icon, parentMenuIcon]);

  const isActiveOrContainsActive = useMemo(() => {
    const checkActiveInMenu = (menu: IMenuItem): boolean => {
      if (menu.route === activeMenuPath || menu.path === activeMenuPath) {
        return true;
      }

      if (menu.childMenus) {
        return menu.childMenus.some((child) => checkActiveInMenu(child));
      }
      return false;
    };

    if (activeMenuPath === "/") return false;

    return checkActiveInMenu(menuItem);
  }, [menuItem, activeMenuPath]);

  const shouldBeOpen = useMemo(() => {
    const manuallyOpened = openMenus[level] === menuItem.name;
    const shouldDefaultOpen =
      isActiveOrContainsActive && openMenus[level] === undefined;
    return manuallyOpened || shouldDefaultOpen || false;
  }, [openMenus, level, menuItem.name, isActiveOrContainsActive]);

  const handleOpenMenu = (open: boolean) => {
    setOpenMenus((prev) => {
      const newState = { ...prev };

      if (open) {
        newState[level] = menuItem.name;

        Object.keys(newState).forEach((levelKey) => {
          const levelNum = parseInt(levelKey);
          if (levelNum > level) {
            newState[levelNum] = null;
          }
        });
      } else {
        newState[level] = null;
        Object.keys(newState).forEach((levelKey) => {
          const levelNum = parseInt(levelKey);
          if (levelNum > level) {
            newState[levelNum] = null;
          }
        });
      }

      return newState;
    });
  };

  // if (menuItem?.name?.toLowerCase() === "dashboard") {
  //   return null;
  // }

  if (hasChildren) {
    return (
      <SubMenu
        className={className}
        key={menuItem.menuKey}
        label={menuItem.name}
        icon={
          level === 0 && (
            <i
              className={`bi ${iconClassName}`}
              style={{ color: "currentcolor" }}
            />
          )
        }
        active={isActiveOrContainsActive}
        open={shouldBeOpen}
        onOpenChange={handleOpenMenu}
      >
        {menuItem.childMenus?.map((childItem) => (
          <RecursiveMenuItem
            key={childItem.menuKey}
            menuItem={childItem}
            activeMenuPath={activeMenuPath}
            level={level + 1}
            parentMenuIcon={menuItem.icon}
            openMenus={openMenus}
            setOpenMenus={setOpenMenus}
          />
        ))}
      </SubMenu>
    );
  }

  return (
    <MenuItem
      title={menuItem.name}
      className={className}
      component={<Link to={menuItem.route || "#"} target={menuItem.route?.toLowerCase()?.includes("http") ? "_blank" : "_self"} />}
      icon={
        level === 0 && (
          <i
            className={`bi ${iconClassName}`}
            style={{ color: "currentcolor" }}
          />
        )
      }
      key={menuItem.menuKey}
      active={
        activeMenuPath === menuItem.route || activeMenuPath === menuItem.path
      }
    >
      {menuItem.name}
    </MenuItem>
  );
};

const Sidebar: React.FC<SidebarProps> = ({ isCollapsed }) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const role = useAppSelector(selectedRoleId);
  const permissionsList = useAppSelector(getPermissionsList);
  const [activeMenuPath, setActiveMenuPath] = useState<string>();
  const [openMenus, setOpenMenus] = useState<Record<number, string | null>>({});
  const [menuItemList, setMenuItemsList] = useState<IMenuItem[]>(useAppSelector(getMenuItems) ?? []);
  const { getRolesPermissionAPI, getRolesPermissionLoading } = useRolesAndPermissions();
  const { getPermissionsAPI, getPermissionsLoading } = useAdmin();

  useEffect(() => {
    if (menuItemList && menuItemList.length <= 0) {
      getPermissionsAPI(role).then((res) => {
        if (res?.length > 0) {
          dispatch(setMenuItems(res));
          setMenuItemsList(res);
        }
      });
    }
    if (permissionsList && permissionsList.length <= 0) {
      getPermissions();
    }
  }, [menuItemList, permissionsList, role]);

  useEffect(() => {
    if (menuItemList.length > 0) {
      const { subMenuDetails }: any = getSideBarActiveDetails(
        location.pathname
      );
      setActiveMenuPath(subMenuDetails?.path || subMenuDetails?.route);
    }
    if (location.pathname === "/") {
      setOpenMenus({});
    }
  }, [location.pathname, menuItemList]);

  const getPermissions = () => {
    getRolesPermissionAPI({ roleId: role }).then((data: any) => {
      const groupedData = data
        ?.map((item: { permission: any }) => item.permission)
        ?.filter(
          (x: { isMenu: boolean; isActive: boolean }) =>
            x.isMenu === false && x.isActive === true
        );
      if (groupedData?.length > 0) {
        dispatch(setPermissions(groupedData));
      }
    });
  };

  return (
    <>
      {getPermissionsLoading || getRolesPermissionLoading ? <Loading /> : null}
      <Sidebr
        rootStyles={{
          backgroundColor: "transparent",
          marginTop: "1rem",
          border: "none",
        }}
        style={{ height: "calc(100vh - 100px)" }}
        collapsed={isCollapsed}
      >
        <Menu
          closeOnClick
          menuItemStyles={{
            button: {
              [`&.active`]: {
                backgroundColor: "#E6F7FE",
                color: "#044D6E",
                fontWeight: "600",
              },
              color: "#333333",
              fontSize: "0.875rem",
              borderRadius: "4px",
              margin: "0 16px",
              height: "35px",
              padding: "8px 0px",
              [`&:hover`]: {
                color: "#044D6E",
              },
            },
          }}
        >
          {/* <MenuItem
            title={"Dashboard"}
            className={"main-menu-items"}
            component={<Link to={"/"} />}
            icon={
              <i
                className="bi bi-speedometer2"
                style={{ color: "currentcolor", fontSize: 20 }}
              ></i>
            }
            active={(activeMenuPath || location.pathname) === "/"}
          >
            Dashboard
          </MenuItem> */}
          {menuItemList?.map(
            (menuItem: IMenuItem) =>
              menuItem.name && (
                <RecursiveMenuItem
                  key={menuItem.menuKey}
                  menuItem={menuItem}
                  activeMenuPath={activeMenuPath}
                  parentMenuIcon={menuItem.icon}
                  openMenus={openMenus}
                  setOpenMenus={setOpenMenus}
                />
              )
          )}
        </Menu>
      </Sidebr>
    </>
  );
};

export default Sidebar;
